
import {
  defineComponent, nextTick, reactive, watch,
} from 'vue';
import Card from '@/core/components/Tela/Card.vue';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';
import SelecionarCstIcms from '@/components/Cadastros/Tributacoes/CenariosFiscais/SelecionarCstIcms.vue';
import SelecionarCstIpi from '@/components/Cadastros/Tributacoes/CenariosFiscais/SelecionarCstIpi.vue';
import SelecionarCstPis from '@/components/Cadastros/Tributacoes/CenariosFiscais/SelecionarCstPis.vue';
import SelecionarCstCofins from '@/components/Cadastros/Tributacoes/CenariosFiscais/SelecionarCstCofins.vue';
import SelecionarOrigemMercadoria from '@/components/Cadastros/Tributacoes/CenariosFiscais/SelecionarOrigemMercadoria.vue';
import SelecionarModalidadeBaseCalculoIcms from '@/components/Cadastros/Tributacoes/CenariosFiscais/SelecionarModalidadeBaseCalculoIcms.vue';
import SelecionarModalidadeBaseCalculoIcmsSubstituicao from '@/components/Cadastros/Tributacoes/CenariosFiscais/SelecionarModalidadeBaseCalculoIcmsSubstituicao.vue';
import SelecionarCodigoEnquadramentoIpi from '@/components/Cadastros/Tributacoes/CenariosFiscais/SelecionarCodigoEnquadramentoIpi.vue';
import SelecionarTipoCalculoImposto from '@/components/Cadastros/Tributacoes/CenariosFiscais/SelecionarTipoCalculoImposto.vue';
import SelecionarCest from '@/components/Cadastros/Produtos/SelecionarCest.vue';
import SelecionarCsosn from '@/components/Cadastros/Tributacoes/CenariosFiscais/SelecionarCsosn.vue';
import { INotaFiscalItemImposto, INotaFiscalItemImpostoIbsCbs } from '@/models/Entidades/Fiscal/INotaFiscal';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import SelecionarSimNao from '@/core/components/Tela/SelecionarSimNao.vue';

export default defineComponent({
  name: 'NotaFiscalItemImpostos',
  props: {
    impostos: {
      type: Object as () => INotaFiscalItemImposto,
      required: true,
    },
    editavel: {
      type: Boolean,
      required: true,
    },
    atualizarApenasInformacoesFiscais: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Card,
    CampoNumerico,
    SelecionarCstIcms,
    SelecionarCstIpi,
    SelecionarCstPis,
    SelecionarCstCofins,
    SelecionarOrigemMercadoria,
    SelecionarModalidadeBaseCalculoIcms,
    SelecionarModalidadeBaseCalculoIcmsSubstituicao,
    SelecionarCodigoEnquadramentoIpi,
    SelecionarTipoCalculoImposto,
    SelecionarCest,
    SelecionarCsosn,
    SelecionarSimNao,
  },
  emits: ['update:impostos', 'calcular', 'calcularTributacoes'],
  setup(props, { emit }) {
    const state = reactive({
      definirCst: true,
      localImpostos: UtilitarioGeral.clonarObjeto(props.impostos) as INotaFiscalItemImposto,
    });

    let syncingFromParent = false;

    function inicializarIbsCbs() {
      if (!state.localImpostos.ibsCbs) {
        state.localImpostos.ibsCbs = {
          codigo: 0,
          codigoNotaFiscalItemImposto: 0,
          codigoSituacaoTributaria: '',
          codigoClassificacaoTributaria: '',
          indicadorDoacao: 0,
          valorBaseCalculoIbsCbs: 0,
          aliquotaIbsUf: 0,
          percentualReducaoAliquotaIbsUf: 0,
          aliquotaEfetivaIbsUf: 0,
          valorIbsUf: 0,
          valorDiferimentoIbsUf: 0,
          valorDevolucaoTributoIbsUf: 0,
          aliquotaIbsMunicipio: 0,
          percentualReducaoAliquotaIbsMunicipio: 0,
          aliquotaEfetivaIbsMunicipio: 0,
          valorIbsMunicipio: 0,
          valorDiferimentoIbsMunicipio: 0,
          valorDevolucaoTributoIbsMunicipio: 0,
          aliquotaCbs: 0,
          percentualReducaoAliquotaCbs: 0,
          aliquotaEfetivaCbs: 0,
          valorCbs: 0,
          valorDiferimentoCbs: 0,
          valorDevolucaoTributoCbs: 0,
          valorIbsTotal: 0,
        } as INotaFiscalItemImpostoIbsCbs;
      }
    }

    watch(
      () => props.impostos,
      (newValue) => {
        syncingFromParent = true;
        state.localImpostos = UtilitarioGeral.clonarObjeto(newValue);
        state.definirCst = UtilitarioGeral.valorValido(state.localImpostos.cst);
        console.log('ibsCbs: ', state.localImpostos.ibsCbs);
        inicializarIbsCbs();
        nextTick(() => {
          syncingFromParent = false;
        });
      },
      { deep: true, immediate: true },
    );

    watch(
      state.localImpostos,
      (newValue) => {
        if (syncingFromParent) return;
        emit('update:impostos', newValue);
      },
      { deep: true, flush: 'post' },
    );

    function atualizarTotalImpostos() {
      state.localImpostos.valorTotalTributos = 0;

      state.localImpostos.valorTotalTributos += state.localImpostos.icms.valorIcms;
      state.localImpostos.valorTotalTributos += state.localImpostos.ipi.valorIpi;
      state.localImpostos.valorTotalTributos += state.localImpostos.pis.valorPis;
      state.localImpostos.valorTotalTributos += state.localImpostos.cofins.valorCofins;
      state.localImpostos.valorTotalTributos += state.localImpostos.importacao.valorImportacao;

      if (state.localImpostos.ibsCbs) {
        state.localImpostos.valorTotalTributos += state.localImpostos.ibsCbs.valorIbsUf || 0;
        state.localImpostos.valorTotalTributos += state.localImpostos.ibsCbs.valorIbsMunicipio || 0;
        state.localImpostos.valorTotalTributos += state.localImpostos.ibsCbs.valorCbs || 0;
      }
    }

    function calcular() {
      if (props.atualizarApenasInformacoesFiscais) return;
      emit('calcular');
    }

    function calcularTributacoes() {
      if (props.atualizarApenasInformacoesFiscais) return;
      emit('calcularTributacoes');
    }

    function definePreenchimentoCstCsosn() {
      if (state.definirCst) {
        state.localImpostos.cson = '';
      } else {
        state.localImpostos.cst = '';
      }
    }

    function verificaEdicaoCampoEInformacaoFiscal(): boolean {
      if (props.atualizarApenasInformacoesFiscais) {
        return false;
      }
      return !props.editavel;
    }

    return {
      props,
      state,
      atualizarTotalImpostos,
      calcular,
      calcularTributacoes,
      definePreenchimentoCstCsosn,
      verificaEdicaoCampoEInformacaoFiscal,
    };
  },
});
